import { useApolloClient } from '@apollo/client';
import { cacheAddCurrentUserGuide, cacheAddCurrentUserMap } from 'cache/cacheUpdateMethods';
import {
    LinkWorldMapToUser,
    LinkWorldMapToUserVariables
} from 'graphql/mutation/__generated__/LinkWorldMapToUser';
import { LINK_WORLD_MAP_TO_USER_MUTATION } from 'graphql/mutation/worldMutation';
import {
    GuideByIdQuery,
    GuideByIdQueryVariables
} from 'graphql/query/__generated__/GuideByIdQuery';
import { WorldMap, WorldMapVariables } from 'graphql/query/__generated__/WorldMap';
import { GUIDES_BY_ID_QUERY } from 'graphql/query/guideQuery';
import { WORLD_MAP_QUERY } from 'graphql/query/worldQuery';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { useContext, useEffect } from 'react';
import { GlobalContext } from 'session/state/context';

export const useLinkMapAndGuideToUser = () => {
    const globalContext = useContext(GlobalContext);
    const apollo = useApolloClient();

    const { getTokenForMapId, getListOfMaps, removeTokenForMapId, removeTokenForGuideId } =
        useWorldMapLocalStorage();

    const worldMapIds = getListOfMaps();
    const loggedInUserId = globalContext.state.loggedUser?.userId;

    useEffect(() => {
        if (!loggedInUserId) {
            return;
        }

        worldMapIds.forEach(async (worldMapId) => {
            const map = await apollo.query<WorldMap, WorldMapVariables>({
                query: WORLD_MAP_QUERY,
                variables: {
                    worldMapId: worldMapId,
                    token: getTokenForMapId(worldMapId)
                }
            });

            const userId = map.data?.worldMap?.user?.userId;
            if (userId) {
                removeTokenForMapId(worldMapId);
                if (map.data?.worldMap?.guideId) {
                    removeTokenForGuideId(map.data?.worldMap?.guideId);
                }
                return;
            }

            await apollo.mutate<LinkWorldMapToUser, LinkWorldMapToUserVariables>({
                mutation: LINK_WORLD_MAP_TO_USER_MUTATION,
                variables: {
                    worldMapId: worldMapId,
                    token: getTokenForMapId(worldMapId),
                    userId: loggedInUserId
                }
            });

            if (map.data?.worldMap?.guideId) {
                const guide = await apollo.query<GuideByIdQuery, GuideByIdQueryVariables>({
                    query: GUIDES_BY_ID_QUERY,
                    variables: {
                        guideId: map.data.worldMap.guideId
                    }
                });
                cacheAddCurrentUserGuide(apollo.cache, guide.data.guideById);
            }
            cacheAddCurrentUserMap(apollo.cache, map.data.worldMap);

            removeTokenForMapId(worldMapId);
            if (map.data?.worldMap?.guideId) {
                removeTokenForGuideId(map.data?.worldMap?.guideId);
            }
        });
    }, [loggedInUserId, worldMapIds.join(';')]);

    return null;
};
